import React, { useCallback } from 'react';
import classes from './style.module.scss';
import Button from 'app/common/Button/index';
import useTranslation from 'app/hooks/useTranslation';
import useForm, { FormValues } from "~/app/modules/form/useForm";
import { IssueClosePayload, APIResponse, IssueDetails, Machine } from "~/types";
import { useStoreActions, useStoreState } from "~/store";
import { toast } from "react-toastify";
import { checkIsObjectPropertiesEqual } from '~/app/helpers/object';
import { notify } from '~/app/helpers/notify';
import { useParams } from "react-router-dom";

export interface FooterProps {
    onClose: () => void
    issueDetails: IssueDetails
    defaultFormValues?: FormValues<IssueClosePayload>
    onSave?: (id: IssueDetails['id']) => void
}

export type FooterComponent = React.FC<FooterProps>;

const Footer: FooterComponent = ({ onClose, issueDetails, defaultFormValues, onSave }) => {
    const { t } = useTranslation('issue');
    const { id } = useParams<{ id: Machine['id'] }>();
    const { values: formValues, formEvents: { onSubmit }, formState } = useForm<IssueClosePayload>({ formName: 'completeIssue' });
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const completeIssueAction = useStoreActions(actions => actions.issue.completeIssue);
    const issueUpdating = useStoreState(state => state.issue.issueUpdating);
    const MachineOfflineFetchAction = useStoreActions(actions => actions.machine.fetchOfflineMachine);
    const MachineInFavorite = useStoreState(state => state.machines.inFavorite);
    const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const userName = useStoreState(state => state.user.data.name);
    const issueId = issueDetails.id;
    const workOrderId = issueDetails.workOrderId;

    const saveClickHandler = useCallback((): void => {

        onSubmit(({ formState: { invalid } }) => {
            if (invalid) {
                toast.error(t('error.required'));
                return;
            }
            const payload: IssueClosePayload = {
                ...formValues,
                ...(issueId && { issueId }),
                ...(workOrderId && { workOrderId })
            } as IssueClosePayload;

            
            completeIssueAction(payload).then(async (data: APIResponse) => {
                onClose();
                issueId && onSave?.(issueId);
                data.success ? notify(t(`success.completed`), 'success') : notify(t(`error.${data?.msg}`, {userName: data?.msg && data.msg === 'userAlreadyExistForSelectedDate' ? userName : ''}), 'error');
                await (netWorkStatus ? MachineOfflineFetchAction : MachineFetchAction)(id ?? issueDetails?.machineId);
                (netWorkStatus === 0 && MachineInFavorite(id ?? issueDetails?.machineId));
            });
        })();
    }, [onSubmit, formValues, issueId, workOrderId, completeIssueAction, t, onClose, onSave, userName, netWorkStatus, MachineOfflineFetchAction, MachineFetchAction, id, issueDetails?.machineId, MachineInFavorite]);

    const isDisabled = (): boolean => {
        let isDisabled = true;

        if (
            formValues.comment &&
            formValues?.ordinaryHours &&
            formValues?.workType &&
            (!isHPCodeRequired || formValues?.hpCode) &&
            formState.touched && !formState.invalid
        ) {
            isDisabled = false;
        }

        if (issueId && formValues && defaultFormValues && !isDisabled) {
            const keysToCheck = [
                "comment",
                "ordinaryHours",
                "workType",
                ...(isHPCodeRequired ? ['hpCode'] : [])
            ];
            isDisabled = checkIsObjectPropertiesEqual(defaultFormValues, formValues, keysToCheck);
        }
        return isDisabled;
    }

    return (
        <div className={`${classes['footer']}`}>
            <Button
                className={classes['abort']}
                onClick={onClose}>{t('cancel')}</Button>
            <Button
                className={classes['submit']}
                disabled={isDisabled()}
                loading={issueUpdating}
                onClick={saveClickHandler}>{t('issueClose.closeTheIssue')}</Button>
        </div>
    );
};

export default Footer
